import type { FC } from 'react';
import { useState } from 'react';
import type { BeneficialOwnerResponse, IANATimezones } from '@gcv/shared';
import ArrowForwardIos from '@mui/icons-material/ArrowForwardIos';
import { Box } from '@mui/material';

import { useCrbBeneficialOwners, useCrbSetManagingControlProng } from 'tanstack-hooks/crb-beneficial-owners';
import { Select, Spinner, Text } from 'ui';
import { CrbBeneficialOwnerDrawer } from 'ui/apps/crb/company-profile/components/crb-beneficial-owner-drawer';
import { CrbBeneficialOwnerModal } from 'ui/apps/crb/company-profile/components/crb-beneficial-owner-modal';
import palette from 'ui/theme/palette';
import { DateTimeHelpers } from 'util/dateTime.util';
import { getBeneficialOwnerQuestionnaireRowBlurb } from './util/get-beneficial-owner-questionnaire-row-blurb';

interface Props {
  crbId: string;
  templateResultId: string;
  timezone: IANATimezones;
  questionnaireBankId?: string;
  selectedManagingIndividualId: string | undefined;
  fieldWidth?: string | { xs?: string; sm?: string; md?: string; lg?: string };
}

export const CustomFormManagingIndividual: FC<Props> = ({
  crbId,
  templateResultId,
  timezone,
  questionnaireBankId,
  selectedManagingIndividualId,
  fieldWidth
}) => {
  const [isAddOwnerOpen, setIsAddOwnerOpen] = useState(false);
  const [viewedOwnerId, setViewedOwnerId] = useState<string>();

  const { beneficialOwners, isLoadingBeneficialOwners } = useCrbBeneficialOwners(crbId);
  const { setManagingControlProng } = useCrbSetManagingControlProng(crbId);

  const activeOwners = beneficialOwners.filter((owner) => !owner.archived);
  const selectedOwner = activeOwners.find((owner) => owner.id === selectedManagingIndividualId);
  const viewedOwner = beneficialOwners.find((owner) => owner.id === viewedOwnerId);
  const options = [
    ...activeOwners.map((owner) => ({
      label: [owner.beneficial_owner_first_name, owner.middle_initial, owner.beneficial_owner_last_name]
        .filter(Boolean)
        .join(' '),
      value: owner.id
    })),
    {
      label: '+ Add a Controlling/Managing Individual',
      value: 'add-beneficial-owner',
      action: () => setIsAddOwnerOpen(true)
    }
  ];

  const selectOwner = (owner: BeneficialOwnerResponse) => {
    setManagingControlProng({
      beneficialOwnerId: owner.id,
      templateResultId,
      confirmation: true
    });
  };

  const handleChange = (event: { target: { value: string } }) => {
    const owner = activeOwners.find((beneficialOwner) => beneficialOwner.id === event.target.value);
    if (owner) selectOwner(owner);
  };

  if (isLoadingBeneficialOwners) return <Spinner />;

  return (
    <>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ marginBottom: 1, width: fieldWidth }}>
          <Select
            dataCy="managing-individual-select"
            emptyLabel="Select a Controlling/Managing Individual"
            label="Controlling/Managing Individual"
            name="managing-individual"
            onChange={handleChange}
            options={options}
            value={selectedManagingIndividualId ?? ''}
          />
        </Box>
        {selectedOwner && (
          <Box
            onClick={() => setViewedOwnerId(selectedOwner.id)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setViewedOwnerId(selectedOwner.id);
              }
            }}
            role="button"
            sx={{
              alignItems: 'center',
              border: `1px solid ${palette.divider}`,
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              gap: 2,
              minHeight: '72px',
              padding: '10px 24px',
              width: fieldWidth,
              '&:hover': {
                backgroundColor: palette.background.default
              }
            }}
            tabIndex={0}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Text
                  content={[
                    selectedOwner.beneficial_owner_first_name,
                    selectedOwner.middle_initial,
                    selectedOwner.beneficial_owner_last_name
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  type="body1"
                  sx={{ fontWeight: 700 }}
                />
                {getBeneficialOwnerQuestionnaireRowBlurb(selectedOwner)}
              </Box>
              <Text
                content={`${
                  selectedOwner.job_title_occupation || 'Job title not provided'
                } · DOB ${DateTimeHelpers.formatDateStringToFriendlyDateString(
                  selectedOwner.date_of_birth,
                  timezone
                )}`}
                type="body2"
                sx={{ color: palette.text.secondary, marginTop: '2px' }}
              />
            </Box>
            <Text
              content={`${selectedOwner.ownership_in_this_business}%`}
              type="body1"
              sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
            />
            <ArrowForwardIos sx={{ color: palette.text.disabled, fontSize: '18px' }} />
          </Box>
        )}
      </Box>
      <CrbBeneficialOwnerModal
        isOpen={isAddOwnerOpen}
        onClose={() => setIsAddOwnerOpen(false)}
        onOwnerCreated={selectOwner}
        questionnaireBankId={questionnaireBankId}
        config={{ addOrEdit: 'add', owner: undefined }}
      />
      {viewedOwner && (
        <CrbBeneficialOwnerDrawer
          open={!!viewedOwner}
          onClose={() => setViewedOwnerId(undefined)}
          owner={viewedOwner}
          questionnaireBankId={questionnaireBankId}
        />
      )}
    </>
  );
};
